import { Component } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'

function reportError(error, info, userId) {
  fetch(`${API_URL}/client-errors`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message: error?.message || String(error),
      stack: error?.stack,
      componentStack: info?.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: userId || null,
    }),
  }).catch(() => {})
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
    this.reset = this.reset.bind(this)
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info.componentStack)
    reportError(error, info, this.props.userId)
  }

  reset() {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-gold/40" />
            <div className="w-11 h-11 rounded-full bg-risk-red/10 border border-risk-red/20 flex items-center justify-center">
              <svg
                width="20" height="20" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="1.5"
                className="text-risk-red"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-gold/40" />
          </div>

          <h2 className="font-serif text-3xl text-cream mb-3">Something went wrong</h2>
          <p className="text-sm text-parchment/60 mb-8 mx-auto">
            An unexpected error occurred on this page. Your data is safe — this is a display error only.
          </p>

          <div className="flex items-center justify-center gap-3">
            <button
              onClick={this.reset}
              className="px-5 py-2.5 bg-gold text-navy text-sm font-bold tracking-wider rounded-lg hover:bg-gold-light transition-colors cursor-pointer border-0"
            >
              Try Again
            </button>
            <a
              href="/"
              className="px-5 py-2.5 border border-slate-border text-parchment/70 text-sm font-bold tracking-wider rounded-lg hover:border-gold/30 hover:text-cream transition-colors no-underline"
            >
              Go to Homepage
            </a>
          </div>

          {import.meta.env.DEV && this.state.error && (
            <details className="mt-8 text-left bg-navy-lighter rounded-xl p-4 border border-slate-border">
              <summary className="text-xs text-sepia cursor-pointer select-none">
                Error details (dev only)
              </summary>
              <pre className="text-xs text-risk-red mt-3 overflow-auto whitespace-pre-wrap font-mono leading-relaxed">
                {this.state.error.toString()}
              </pre>
            </details>
          )}
        </div>
      </div>
    )
  }
}
